/**
 * @fileoverview Connect matter-linked clients to explicit contact edits and portal tools. The backend owns client identity and accepted writes; the parent refreshes case state. `PortalInvitations.js` owns document upload links. All payment request and verification controls belong to the Payment Instructions tab.
 */

import React, { useState, useEffect, useContext } from 'react';
import ToastContext from '../../ToastContext';
import api from '../../api';
import OpposingCounsel from './OpposingCounsel';
import PortalInvitations from './PortalInvitations';
import { AppButton, AppTextInput, AppTextarea, AppToggle, AppTabView, AppTab } from '../shared';
import './PartiesPanel.css';
import AppIcon from "../shared/AppIcon";




function Clients({ clients, setClients, opposingCounsel, setOpposingCounsel, matterNumber, fetchMatter }) {
  const [partyTabIndex, setPartyTabIndex] = useState(0);
  const [clientTabIndex, setClientTabIndex] = useState(0);
  const [isEditing, setIsEditing] = useState(false);
  const [pendingFocusField, setPendingFocusField] = useState(null);
  const showToast = useContext(ToastContext);

  const addNewClient =

  () => {
    const newClient = {
      ClientName: "",
      ClientPhone: "",
      ClientEmail: "",
      AdditionalPhone: "",
      AdditionalEmails: "",
      Notes: "",
      PrimaryClient: false,
      IsNew: true
    };

    setClients([...clients, newClient]);
    setPartyTabIndex(0);
    setClientTabIndex(clients.length);
    setPendingFocusField('ClientName');
    setIsEditing(true);
  };

  const setClient =

  (updatedClient, index) => {
    const newClients = [...clients];
    newClients[index] = updatedClient;
    setClients(newClients);
  };

  const removeClient =

  (index) => {
    const newClients = [...clients];
    newClients.splice(index, 1);
    setClients(newClients);
    setClientTabIndex((currentIndex) => {
      if (currentIndex === index) {
        return 0;
      }
      if (currentIndex > index) {
        return currentIndex - 1;
      }
      return currentIndex;
    });
  };

  const handleSave =

  async () => {
    setIsEditing(false);
    setPendingFocusField(null);
    try {
      // Remove isNew property from clients and add MatterNumber
      const clientsToSave = clients.map((client) => {
        const { IsNew, ...rest } = client;
        rest.MatterNumber = matterNumber;
        return rest;
      });
      const response = await api.post('clients', clientsToSave);
      fetchMatter();

      if (response.status !== 201) {// assuming 201 for successful resource creation
        showToast({ severity: 'error', summary: 'Error', detail: 'Failed to update client information' });
        return;
      }

      showToast({ severity: 'success', summary: 'Successful', detail: 'Updated client information' });
    } catch (error) {
      console.error(error);
      showToast({ severity: 'error', summary: 'Error', detail: 'An error occurred while updating client information' });
    }
  };

  const handleCancel =

  async () => {
    setPendingFocusField(null);
    await fetchMatter();
    setIsEditing(false);
  };

  const handleStartEditingField =

  (fieldKey) => {
    if (!isEditing) {
      setPendingFocusField(fieldKey);
      setIsEditing(true);
    }
  };


  return (
    <div className="col-12 xl:col-6">
			<div className={`parties-panel__shell parties-panel${isEditing ? ' is-editing' : ''}`}>
				<div className="parties-panel__tabs">
					<AppTabView
            activeIndex={partyTabIndex}
            onTabChange={(e) => setPartyTabIndex(e.index)}
            className="parties-panel__primary-tabs">

						<AppTab header="Clients">
							<div className="parties-panel__nested-tabs">
								<AppTabView
                  activeIndex={clientTabIndex}
                  onTabChange={(e) => setClientTabIndex(e.index)}
                  className="parties-panel__secondary-tabs">

									{clients.map((client, index) =>
                  <AppTab header={<span className="parties-panel__client-tab-label">
                    <span>{client.IsNew ? "New Client" : client.ClientName || `Client ${index + 1}`}</span>
                    {client.PrimaryClient === true && <span className="parties-panel__primary-indicator"><AppIcon icon="pi pi-check" aria-hidden="true" />Primary</span>}
                  </span>} key={index}>
											<div className="parties-panel__tab-panel">
												<ClientInfo
                        client={client}
                        matterNumber={matterNumber}
                        setClient={(updatedClient) => setClient(updatedClient, index)}
                        isEditing={isEditing}
                        setIsEditing={setIsEditing}
                        pendingFocusField={pendingFocusField}
                        setPendingFocusField={setPendingFocusField}
                        removeClient={() => removeClient(index)}
                        onCancel={handleCancel}
                        onSave={handleSave}
                        onStartEditingField={handleStartEditingField} />

											</div>
										</AppTab>
                  )}
									<AppTab
                    action
                    alignRight
                    ariaLabel="Add Client"
                    header={<span className="parties-panel__add-tab-label"><AppIcon icon="pi pi-plus" aria-hidden="true" /><span>Add Client</span></span>}
                    onClick={addNewClient} />

								</AppTabView>
							</div>
						</AppTab>
						<AppTab header="Opposing Counsel">
							<OpposingCounsel
                tabbed
                opposingCounsel={opposingCounsel}
                setOpposingCounsel={setOpposingCounsel}
                matterNumber={matterNumber}
                fetchMatter={fetchMatter} />

						</AppTab>
					</AppTabView>
				</div>
			</div>
		</div>);

}




function ClientInfo({ client, matterNumber, setClient, isEditing, setIsEditing, pendingFocusField, setPendingFocusField, removeClient, onCancel, onSave, onStartEditingField }) {
  const attributes = [
  { label: "Name", key: "ClientName", value: client.ClientName },
  { label: "Phone", key: "ClientPhone", value: client.ClientPhone },
  { label: "Email", key: "ClientEmail", value: client.ClientEmail },
  { label: "Additional Phones", key: "AdditionalPhone", value: client.AdditionalPhone },
  { label: "Additional Emails", key: "AdditionalEmails", value: client.AdditionalEmails },
  { label: "Notes", key: "Notes", value: client.Notes, fullWidth: true }];


  if (client.PipedriveID) {
    attributes.push({ label: "Pipedrive ID", key: "PipedriveID", value: client.PipedriveID });
  } else {
    attributes.push({ label: "Pipedrive ID", key: "PipedriveID", value: "" });
  }
  if (client.FileUploadURL) {
    attributes.push({ label: "File Upload URL", key: "FileUploadURL", value: client.FileUploadURL, fullWidth: true });
  }

  const handleInputChange =

  (e, attributeKey) => {
    let newValue;
    if (attributeKey === 'PrimaryClient') {
      newValue = e.value; // e.value will be true or false
    } else {
      newValue = e.target?.value !== undefined ? e.target.value : e;
    }
    const newClient = { ...client };
    newClient[attributeKey] = newValue;
    setClient(newClient);
  };


  useEffect(() => {
    console.log("Client:", client);
  }, [client]);

  useEffect(() => {
    if (isEditing && pendingFocusField) {
      setPendingFocusField(null);
    }
  }, [isEditing, pendingFocusField, setPendingFocusField]);


  return (
    <div className="parties-panel__client-info">
			{isEditing && <div className="parties-panel__client-topline">
				<div className="parties-panel__client-primary">
					<span className="parties-panel__inline-label">Primary Client</span>
					<AppToggle
            checked={client.PrimaryClient === true}
            disabled={!isEditing}
            onChange={(e) => handleInputChange(e, 'PrimaryClient')} />

				</div>
				{isEditing ?
        <div className="parties-panel__client-actions">
						<AppButton
            className="parties-panel__button parties-panel__button--danger-muted"
            icon="pi pi-times"
            label="Detach client"
            variant="secondary"
            onClick={(e) => {
              e.stopPropagation();
              removeClient();
            }} />

						<AppButton
            className="parties-panel__button"
            aria-label="Cancel editing clients"
            icon="pi pi-times"
            label="Cancel"
            variant="secondary"
            onClick={(e) => {
              e.stopPropagation();
              onCancel();
            }} />

						<AppButton
            aria-label="Save clients"
            className="parties-panel__button"
            icon="pi pi-check"
            label="Save"
            variant="success"
            onClick={(e) => {
              e.stopPropagation();
              onSave();
            }} />

					</div> :
        null}
			</div>}
      {!client.IsNew && !isEditing && <PortalInvitations key={`${matterNumber}:${client.id}`} matterNumber={matterNumber} clientId={client.id} clientName={client.ClientName} />}
			<div className={`parties-panel__field-grid${isEditing ? '' : ' parties-panel__field-grid--interactive'}`}>
				{attributes.map((attribute) =>
        <div
          key={attribute.key}
          className={`parties-panel__field${attribute.fullWidth ? ' parties-panel__field--full' : ''}${attribute.key === 'Notes' ? ' parties-panel__field--tall' : ''}`}
          onClick={() => onStartEditingField(attribute.key)}>

						<div className="parties-panel__field-label">
							{attribute.label}
						</div>
						<div className={isEditing ? 'parties-panel__field-control' : 'parties-panel__field-value'}>
							{isEditing ?
            attribute.key === 'Notes' ?
            <AppTextarea
              variant="borderless"
              autosize
              className="parties-panel__compact-textarea"
              rows={1}
              autoFocus={pendingFocusField === attribute.key}
              value={attribute.value || ''}
              onChange={(val) => handleInputChange({ target: { value: val } }, attribute.key)} /> :

            attribute.key === 'FileUploadURL' ?
            <FileUploadURL attribute={attribute} /> :

            <AppTextInput
              className="parties-panel__compact-input"
              variant="borderless"
              autoFocus={pendingFocusField === attribute.key}
              value={attribute.value || ''}
              onChange={(e) => handleInputChange(e, attribute.key)} /> :


            attribute.value === null || attribute.value === undefined || attribute.value === '' ?
            attribute.key === 'PipedriveID' ?
            <span className="parties-panel__field-text parties-panel__field-text--muted"><AppIcon icon="pi pi-exclamation-triangle text-yellow-500" /> Pipedrive ID is needed for File Upload URL</span> :
            <span className="parties-panel__field-text parties-panel__field-text--muted">Not Available</span> :


            attribute.key === 'FileUploadURL' ?
            <FileUploadURL attribute={attribute} /> :
            <span className={`parties-panel__field-text${attribute.key === 'Notes' ? ' parties-panel__field-text--multiline' : ''}`}>
											{attribute.value.toString()}
										</span>}

						</div>
					</div>
        )}
			</div>
		</div>);


}


export default Clients;



function FileUploadURL({ attribute }) {
  const showToast = useContext(ToastContext);

  return (
    <div className="parties-panel__file-url">
			<div className="parties-panel__file-url-text">{attribute.value}</div>
			<AppButton
        variant="secondary"
        icon="pi pi-copy"
        aria-label="Copy file upload URL"
        style={{ padding: '0.38rem 0.52rem' }}
        onClick={(e) => {
          e.stopPropagation();
          navigator.clipboard.writeText(attribute.value);
          showToast({ severity: 'success', summary: 'Copied', detail: 'File Upload URL copied to clipboard' });
        }} />

		</div>);

}
